import moment from 'moment';
import { cloneDeep } from 'lodash';
import { aiAlertMapping } from '@utils/aiAlerts/metrics';
import { humanize } from '@utils';
import { PRODUCT_MODE_MAP } from '@src/utils/constants';
import { getMetricMetaByKey } from '@src/utils/metrics';
import { SeverityText } from '@src/utils/aiAlerts/mapping';

const humanizeAIAlertValue = (value, unit) => {
  if (unit === '%') {
    return (value * 100).toFixed(1);
  }
  return humanize(value, unit);
};
// higher the number, higher the priority
const sortingPriority = {
  Info: 1,
  Warning: 2,
  Critical: 3,
  Ended: 4,
};

export const getLocalTime = (date, tzOffset = null, timeFormat = 'HH:mm ll') => {
  return moment(date).format(timeFormat);
};

export const sortByPriority = (array) => {
  return array.sort(
    (alertEvent1, alertEvent2) =>
      sortingPriority[alertEvent1.eventType] - sortingPriority[alertEvent2.eventType],
  );
};

export const getPeakSeverity = (events) => {
  // ignore ended events
  const validPriorities = events.filter((event) => event.eventType !== 'Ended');
  return validPriorities[validPriorities.length - 1].eventType;
};

export const getImpactedUniquesValue = (events) => {
  return events[events.length - 1].cumulativeImpactedUniques;
};

export const getTotalUniquesValue = (events) => {
  return events[events.length - 1].cumulativeTotalUniques;
};

const formatRootCause = (
  rootCause,
  dimensionName,
  dimensionListObject,
  aiAlertDimensionMapping,
) => {
  const rootCauseArray = rootCause.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');
  let rootCauseFormatted = '';
  let rootCauseWithoutFormatting = '';
  /**
   * https://conviva.atlassian.net/browse/CE-9075
   * adding this flag to determine if the root cause dimension is available for the selected c3
   */
  let areAllRCDimensionsAvailable = true;

  // TODO: build format at render time to avoid dangerously setting html
  rootCauseArray.forEach((element, index) => {
    if (index !== 0) {
      rootCauseFormatted += ' and <br/>';
      rootCauseWithoutFormatting += ' and ';
    }
    const pulse5DimensionMapping = aiAlertDimensionMapping[dimensionNameArray[index]];
    const dimensionLabel = pulse5DimensionMapping
      ? dimensionListObject[pulse5DimensionMapping.field] || pulse5DimensionMapping.pulse5Label
      : '';

    rootCauseFormatted += `<b>${element}</b> (${dimensionLabel})`;
    rootCauseWithoutFormatting += `${element} (${dimensionLabel})`;

    if (!dimensionLabel) {
      areAllRCDimensionsAvailable = false;
    }
  });

  return { rootCauseFormatted, rootCauseWithoutFormatting, areAllRCDimensionsAvailable };
};

const getFilterRule = (rootCause, dimensionName, aiAlertDimensionMapping) => {
  const rootCauseArray = rootCause.split('&&&');
  const dimensionNameArray = dimensionName.split('&&&');
  const filterRuleObject = {};

  rootCauseArray.forEach((rootCause, index) => {
    const mgwDimensionMapping = aiAlertDimensionMapping[dimensionNameArray[index]];
    if (!mgwDimensionMapping) {
      return;
    }
    const { field = '', key = '' } = mgwDimensionMapping ?? {
      field: '',
      key: '',
    };
    filterRuleObject[rootCause] = {
      field,
      op: 'eq',
      value: rootCause,
      key,
      display: rootCause,
      _order: index,
    };
  });

  return filterRuleObject;
};

export const getAlertTagsCountByTabType = (data, tabType) => {
  const countBySeverity = {
    Total: 0,
    Info: 0,
    Warning: 0,
    Critical: 0,
  };

  if (tabType === 'all') {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [key, _value] of Object.entries(countBySeverity)) {
      countBySeverity[key] = data.filter((alert) => alert.peakSeverity === key).length;
    }

    countBySeverity.Total = data.length;
    return countBySeverity;
  } else {
    // const alertType = (tabType == PRODUCT_MODE_MAP.AI) ? 'AD_ALERT' : 'VIDEO_ALERT';
    const tabTypeAlerts = data.filter((alert) => alert.alertType === tabType);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [key, _value] of Object.entries(countBySeverity)) {
      countBySeverity[key] = tabTypeAlerts.filter((alert) => alert.peakSeverity === key).length;
    }

    countBySeverity.Total = tabTypeAlerts.filter((alert) => alert.alertType === tabType).length;
    return countBySeverity;
  }
};

export const getAlertTagsCountBySeverity = (data) => {
  const tabType = {
    all: {},
    EI: {},
    AI: {},
    APP: {},
  };

  const countBySeverity = {
    Total: 0,
    Info: 0,
    Warning: 0,
    Critical: 0,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [key, _value] of Object.entries(countBySeverity)) {
    countBySeverity[key] = data.filter((alert) => alert.peakSeverity === key).length;
  }

  countBySeverity.Total = data.length;

  tabType.all = countBySeverity;
  tabType.EI = getAlertTagsCountByTabType(data, PRODUCT_MODE_MAP.EI);
  tabType.AI = getAlertTagsCountByTabType(data, PRODUCT_MODE_MAP.AI);
  tabType.APP = getAlertTagsCountByTabType(data, PRODUCT_MODE_MAP.APP);

  return tabType;
};

export const getActiveAlertsCount = (data) => {
  const tabType = {
    all: 0,
    EI: 0,
    AI: 0,
    APP: 0,
  };
  const activeAlerts = data.filter((alert) => alert.status === 'Active');
  tabType.all = activeAlerts.length;
  tabType.EI = activeAlerts.filter((it) => it.alertType === PRODUCT_MODE_MAP.EI).length;
  tabType.AI = 0;
  tabType.APP = activeAlerts.filter((it) => it.alertType === PRODUCT_MODE_MAP.APP).length;

  return tabType;
};

export const getAlertTagsCountByMetrics = (data) => {
  const aiAlertMetricKeys = Object.keys(aiAlertMapping);
  const countByMetrics = aiAlertMetricKeys.reduce(
    (counts, key) => {
      counts[key] = 0;
      return counts;
    },
    {} as Record<(typeof aiAlertMetricKeys)[number], number>,
  );

  for (const [key, value] of Object.entries(countByMetrics)) {
    countByMetrics[key] = data.filter((alert) => alert.aiAlertKey === key).length;
  }

  return countByMetrics;
};

export const formatSummaryObject = ({
  alert,
  aiAlertsMetricMeta,
  dimensionListObject,
  customerNames = {},
  dimensionMapping,
}) => {
  const formattedAlert = {} as any;
  const { alertEvents, rootCause, alertType, dimensionName, metricName, rootCauseDisplayLabel } =
    alert;

  const metricNameUpdated = (() => {
    if (alertType === 'AD_ALERT') {
      return `AD_${metricName}`;
    }
    // have already transform it in IF server
    // if (alertType === 'APP_ALERT') {
    //   return `APP_${metricName}`;
    // }
    return metricName;
  })();

  // New in query param returns undefined
  // Eg: "path/New York" gives the dashboard name as undefined york. To resolve this dashboard name is given in caps
  let rootCauseParam = rootCauseDisplayLabel.replaceAll('&&&', ' and ');
  rootCauseParam = rootCauseParam.toUpperCase();
  const { rootCauseFormatted, rootCauseWithoutFormatting, areAllRCDimensionsAvailable } =
    formatRootCause(rootCauseDisplayLabel, dimensionName, dimensionListObject, dimensionMapping);

  const clonedAlertEvents = cloneDeep(alertEvents);
  const filteredEvents = clonedAlertEvents.filter((event) => !!event.eventTime);
  // change it to key, value. O(1) instead of O(n)
  const aiAlertMetric = aiAlertsMetricMeta.find((metric) => metric.pulse4Key === metricNameUpdated);

  let pulse5MetricKey = '';
  if (aiAlertMetric && aiAlertMetric.metricKey) {
    pulse5MetricKey = aiAlertMapping[metricNameUpdated];
  }
  if (!aiAlertMetric && alertType === 'APP_ALERT') {
    pulse5MetricKey = metricName.replace('APP_', '');
  }

  let alertContext = PRODUCT_MODE_MAP.EI;
  if (alertType === 'AD_ALERT') {
    alertContext = PRODUCT_MODE_MAP.AI;
  } else if (alertType === 'APP_ALERT') {
    alertContext = PRODUCT_MODE_MAP.APP;
  }

  let metricInfo = {};

  if (alertType === 'APP_ALERT') {
    metricInfo = getMetricMetaByKey({
      metricKey: pulse5MetricKey,
      context: alertContext,
      defaultValue: {
        displayName: '[DELETED METRIC]',
        unit: '',
      },
    });
  } else {
    const metricsData = aiAlertsMetricMeta.find((metric) => metric.pulse4Key === metricNameUpdated);
    pulse5MetricKey = metricsData.metricKey;
    metricInfo = {
      displayName: metricsData.name,
      unit: metricsData.unit,
    };
  }

  const unit = metricInfo.unit;

  const sortedEvents = sortByPriority(filteredEvents);
  // eslint-disable-next-line prefer-spread
  const peakValue = Math.max.apply(
    Math,
    filteredEvents.map((event) => event.value),
  );
  const peakSeverity = getPeakSeverity(sortedEvents);
  const cumulativeImpactedUniques = getImpactedUniquesValue(sortedEvents);
  const cumulativeTotalUniques = getTotalUniquesValue(sortedEvents);
  formattedAlert.id = alert.alertId;
  formattedAlert.accountName = customerNames[alert.customerId]
    ? customerNames[alert.customerId].accountName
    : '';
  formattedAlert.c3Name = customerNames[alert.customerId]
    ? customerNames[alert.customerId].c3Name
    : '';
  formattedAlert.alertId = alert.alertId;
  formattedAlert.peakSeverity = peakSeverity;
  formattedAlert.aiAlertKey = alertType === 'AD_ALERT' ? `AD_${metricName}` : metricName;
  formattedAlert.rootCause = rootCauseFormatted;
  formattedAlert.rootCauseParam = rootCauseParam;
  formattedAlert.rootCauseWithoutFormatting = rootCauseWithoutFormatting;
  formattedAlert.areAllRCDimensionsAvailable = areAllRCDimensionsAvailable;
  formattedAlert.peakValue =
    alertType === 'APP_ALERT'
      ? `${humanize(peakValue, unit)}${unit}`
      : `${humanizeAIAlertValue(peakValue, unit)}${unit}`;
  formattedAlert.humanizedValue =
    alertType === 'APP_ALERT'
      ? `${humanize(peakValue, unit)}`
      : `${humanizeAIAlertValue(peakValue, unit)}`;
  formattedAlert.cumulativeImpactedUniquesRaw = cumulativeImpactedUniques;
  formattedAlert.cumulativeImpactedUniques = cumulativeImpactedUniques
    ? humanize(getImpactedUniquesValue(sortedEvents), '')
    : 'NA';
  formattedAlert.cumulativeTotalUniques = cumulativeTotalUniques
    ? humanize(getTotalUniquesValue(sortedEvents), '')
    : 'NA';
  formattedAlert.alertType = alertContext;
  formattedAlert.aiAlertType = alertType;
  formattedAlert.pulse5MetricKey = pulse5MetricKey;

  formattedAlert.duration =
    alertType === 'AD_ALERT' ? 'NA' : timeDifference(alert.startTime, alert.endTime);
  formattedAlert.status = alertType === 'AD_ALERT' ? 'NA' : alert.hasEnded ? 'Ended' : 'Active';
  formattedAlert.eventTime = getLocalTime(new Date(filteredEvents[0].eventTime * 1000));
  formattedAlert.eventTimeRaw = filteredEvents[0].eventTime * 1000;
  formattedAlert.metricDisplayName = metricInfo.displayName || pulse5MetricKey;
  formattedAlert.filterRule = getFilterRule(rootCause, dimensionName, dimensionMapping);
  formattedAlert.issueStartGraphTime = moment.utc(alert.startTime * 1000).format();
  formattedAlert.issueStartTime = getLocalTime(new Date(alert.startTime * 1000));

  clonedAlertEvents.forEach((event) => {
    const graphTime = moment.utc(event.eventTime * 1000).format();
    (event.graphTime = graphTime),
      (event.eventTime = getLocalTime(new Date(event.eventTime * 1000)));
    event.peakValue =
      alertType === 'APP_ALERT'
        ? `${humanize(peakValue, unit)}${unit}`
        : `${humanizeAIAlertValue(event.value, unit)}${unit}`;
    event.peakSeverity = event.alertType;
    event.cumulativeImpactedUniquesRaw = event.cumulativeImpactedUniques;
    event.cumulativeImpactedUniques = event.cumulativeImpactedUniques
      ? humanize(event.cumulativeImpactedUniques, '')
      : 'NA';
  });

  formattedAlert.alertEvents = sortedEvents;

  return formattedAlert;
};

export const formatSummaryData = ({
  response,
  dimensionListObject = {},
  dimensionMapping,
  aiAlertsMetricMeta,
}) => {
  const { alertsSummary, customerNames } = response;
  const summaryData = {} as any;
  const formattedResponse = alertsSummary
    // if all the alertEvents don't have eventTime remove it, because it will cause error in format phase
    .filter((alert) => {
      // getPeakSeverity function will filter out Ended event that will cause error
      return alert.alertEvents.some(
        (alertEvent) => !!alertEvent.eventTime && alertEvent.eventType !== 'Ended',
      );
    })
    .map((alert) => {
      return formatSummaryObject({
        alert,
        aiAlertsMetricMeta,
        dimensionListObject,
        customerNames,
        dimensionMapping,
      });
    });

  summaryData.alerts = formattedResponse;
  return summaryData;
};

const timeDifference = (date1, date2) => {
  if (!date1 || !date2) {
    return '';
  }
  let difference = date2 * 1000 - date1 * 1000;

  const hoursDifference = Math.floor(difference / 1000 / 60 / 60);
  difference -= hoursDifference * 1000 * 60 * 60;

  const minutesDifference = Math.floor(difference / 1000 / 60);
  difference -= minutesDifference * 1000 * 60;

  const secondsDifference = Math.floor(difference / 1000);

  return hoursDifference + 'h ' + minutesDifference + 'm ' + secondsDifference + 's ';
};

export const getDataSource = (
  data,
  tabType,
  selectedTags,
  selectedMetrics,
  sortBy,
  isActiveSelected,
) => {
  let databyTabType = data;
  if (tabType !== 'all') {
    databyTabType = data.filter((item) => item.alertType === tabType);
  }

  selectedTags = selectedTags.filter((tag) => tag !== 'Total');

  // filter by selected severity
  if (selectedTags.length > 0) {
    databyTabType = databyTabType.filter((item) => selectedTags.includes(item.peakSeverity));
  }

  // filter by selected metric
  if (selectedMetrics.length > 0) {
    databyTabType = databyTabType.filter((item) => {
      return selectedMetrics.includes(item.aiAlertKey);
    });
  }

  if (isActiveSelected) {
    databyTabType = databyTabType.filter((item) => item.status === 'Active');
  }

  // format for the sub rows
  const dataSource = databyTabType.map((item, index) => {
    const children = [] as any;
    const peakAlertEvent = item.alertEvents.reduce((prev, current) =>
      prev.value >= current.value ? prev : current,
    );

    if (item.alertEvents) {
      item.alertEvents.forEach((event) => {
        children.push({
          cumulativeImpactedUniques: event.cumulativeImpactedUniques,
          peakValue: event.peakValue,
          peakValueFormatted: event.peakValue,
          peakSeverity: event.eventType,
          eventTime: event.eventTime,
          parentId: item.alertId,
        });
      });
    }
    return {
      key: index,
      peakValueFormatted: `${item.peakValue} at ${peakAlertEvent.eventType}`,
      children: children.reverse(),
      ...item,
    };
  });

  // sort data
  if (sortBy) {
    sortColumn(sortBy, dataSource);
  }

  return dataSource;
};

export const sortColumn = (sortBy: any, dataSource) => {
  const sortByCumulativeImpactedUniques = (item1, item2) => {
    const firstItem = item1.cumulativeImpactedUniquesRaw;
    const secondItem = item2.cumulativeImpactedUniquesRaw;
    return secondItem - firstItem;
  };
  const getMapping = (severity) => {
    switch (severity) {
      case SeverityText.Ended:
        return 0;
      case SeverityText.Info:
        return 1;
      case SeverityText.Warning:
        return 2;
      case SeverityText.Critical:
        return 3;
      default:
        return 0;
    }
  };

  switch (sortBy.key) {
    case 'metricDisplayName':
    case 'status':
    case 'rootCause':
    case 'duration':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1[sortBy.key].toLowerCase() || '';
        const secondItem = item2[sortBy.key].toLowerCase() || '';
        return (
          (sortBy.order === 'asc'
            ? firstItem.localeCompare(secondItem)
            : secondItem.localeCompare(firstItem)) || sortByCumulativeImpactedUniques(item1, item2)
        );
      });

      break;

    case 'peakValue':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1.humanizedValue;
        const secondItem = item2.humanizedValue;
        return sortBy.order === 'asc'
          ? firstItem - secondItem
          : secondItem - firstItem || sortByCumulativeImpactedUniques(item1, item2);
      });

      break;

    case 'cumulativeImpactedUniques':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1.cumulativeImpactedUniquesRaw;
        const secondItem = item2.cumulativeImpactedUniquesRaw;
        return sortBy.order === 'asc' ? firstItem - secondItem : secondItem - firstItem;
      });
      break;

    case 'eventTime':
      dataSource = dataSource.sort((item1, item2) => {
        const isSafari = window.safari !== undefined;
        if (isSafari) {
          const item1EventTime = moment(item1.eventTime, 'HH:mm MMM DD, YYYY').format();
          const item2EventTime = moment(item2.eventTime, 'HH:mm MMM DD, YYYY').format();
          return (
            (sortBy.order === 'asc'
              ? moment(item1EventTime).diff(item2EventTime)
              : moment(item2EventTime).diff(item1EventTime)) ||
            sortByCumulativeImpactedUniques(item1, item2)
          );
        } else {
          return (
            (sortBy.order === 'asc'
              ? moment(item1.eventTime).diff(item2.eventTime)
              : moment(item2.eventTime).diff(item1.eventTime)) ||
            sortByCumulativeImpactedUniques(item1, item2)
          );
        }
      });
      break;

    case 'peakSeverity':
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = getMapping(item1[sortBy.key]);
        const secondItem = getMapping(item2[sortBy.key]);
        return sortBy.order === 'asc' ? secondItem - firstItem : firstItem - secondItem;
      });
      break;

    default:
      dataSource = dataSource.sort((item1, item2) => {
        const firstItem = item1[sortBy.key];
        const secondItem = item2[sortBy.key];
        return sortBy.order === 'asc' ? firstItem - secondItem : secondItem - firstItem;
      });
      break;
  }
};
